import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

import { getImageUrl } from '@apps/main/modules/api/hotels';
import type { HotelIngredient } from '@apps/main/modules/recipe/ingredients';
import { useGlobalCurrencyStore } from '@apps/main/new-desktop/core/stores/globalCurrencyStore';
import { useStatsigEventsStore } from '@apps/main/new-desktop/core/stores/statsigEvents/statsigEventsStore';
import { SuggestionType } from '@apps/main/new-desktop/modules/suggestionTypeSelector';
import {
  useBrowserStore,
  useEditingIngredientStore,
  useItineraryStore,
  useRouterStore,
} from '@apps/main/new-desktop/core/stores';
import { useTripStore } from '@apps/main/new-desktop/core/stores/trip/tripStore';
import { useRecipeStore } from '@apps/main/new-desktop/core/stores/recipe/recipeStore';

import { isFeatureToggleActive } from '@libs/feature-toggle';
import type { IngredientId } from '@libs/trip-planner-interfaces';
import {
  AccommodationType,
  AvailabilityOrder,
  createIngredientId,
} from '@libs/trip-planner-interfaces';
import { exhaustiveGuard, isDefined } from '@libs/utils';

import type {
  ActivitySelectionCardData,
  ActivitySelectionDestinationData,
  ActivitySelectionFilterUpdate,
} from '../../models';
import {
  ActivitySelectionFilter,
  ActivitySelectionViewType,
} from '../../models';
import { useActivitySelectionFiltersStore } from '../activitySelectionFilters/activitySelectionFiltersStore';
import { useActivitySelectionModalsStore } from '../activitySelectionModalsStore';
import { useActivitySelectionPanelsStore } from '../activitySelectionPanelsStore';

import { useActivitySelectionListStore } from './activitySelectionListStore';
import {
  filterActivities,
  getActivitySelectionHeaderData,
  keepWrapperScrollPositionOnTop,
  mapDestinationProductToActivitySelectionCard,
  setFilterText,
  sortActivities,
  toggleType,
} from './activitySelectionStoreLogic';

export const useActivitySelectionStore = defineStore(
  'ActivitySelectionStore',
  () => {
    const recipeStore = useRecipeStore();
    const tripStore = useTripStore();
    const editingIngredientStore = useEditingIngredientStore();
    const activitySelectionFiltersStore = useActivitySelectionFiltersStore();
    const browserStore = useBrowserStore();
    const activitySelectionModalsStore = useActivitySelectionModalsStore();
    const globalCurrencyStore = useGlobalCurrencyStore();
    const itineraryStore = useItineraryStore();
    const activitySelectionPanelsStore = useActivitySelectionPanelsStore();
    const activitySelectionListStore = useActivitySelectionListStore();
    const statsigEventsStore = useStatsigEventsStore();
    const routerStore = useRouterStore();

    // State
    const shouldShowPriceRibbon = ref<boolean>(true);
    const viewType = ref<ActivitySelectionViewType>(
      ActivitySelectionViewType.List
    );
    const listSuggestionType = ref<SuggestionType>(SuggestionType.MostPopular);
    const editingIngredientId = ref<IngredientId>(createIngredientId(''));

    // Actions
    function initializeActivitySelectionView(index: number): void {
      statsigEventsStore.logActivitySelectionInitiated();
      const hotelIngredient = recipeStore.stayOnlyRecipe[
        index
      ] as HotelIngredient;
      editingIngredientStore.setStayEditingIngredient(hotelIngredient);
      editingIngredientId.value = hotelIngredient.ingredientId;
      activitySelectionFiltersStore.setFiltersValuesFromIngredient(
        hotelIngredient
      );
      recipeStore.updateSearchActivities(true);
      activitySelectionListStore.isFirstActivitySelectionListRender = true;
    }

    function resetActivitySelectionView(): void {
      viewType.value = ActivitySelectionViewType.List;
      listSuggestionType.value = SuggestionType.MostPopular;
      editingIngredientId.value = createIngredientId('');
      editingIngredientStore.doneEditingIngredient();
      recipeStore.updateSearchActivities(false);
      activitySelectionModalsStore.closeActivitySelectionModal();
      activitySelectionPanelsStore.closeActivityDetailsPanel();
    }

    function toggleFilters(): void {
      if (browserStore.isSmallerThan1536px) {
        activitySelectionModalsStore.openMobileFiltersModal();
        statsigEventsStore.logActivitySelectionFilterOpened();
      } else {
        if (viewType.value === ActivitySelectionViewType.List) {
          viewType.value = ActivitySelectionViewType.Filters;
          statsigEventsStore.logActivitySelectionFilterOpened();
        } else {
          viewType.value = ActivitySelectionViewType.List;
        }
        keepWrapperScrollPositionOnTop();
      }
    }

    function showPriceRibbon(): void {
      shouldShowPriceRibbon.value = true;
    }

    function hidePriceCalendar(): void {
      shouldShowPriceRibbon.value = false;
    }

    function updateActivitySelectionFilter(
      filterUpdate: ActivitySelectionFilterUpdate
    ): void {
      const stayIngredient = editingIngredientStore.editingStayIngredient;

      if (!stayIngredient) {
        return;
      }

      const type = filterUpdate.type;

      // NOTE: No url filter persistance for activity filters
      switch (type) {
        case ActivitySelectionFilter.ActivityName:
          setFilterText(filterUpdate.value, stayIngredient);
          break;

        case ActivitySelectionFilter.TypeTickets:
          toggleType(AccommodationType.Ticket, stayIngredient);
          break;

        case ActivitySelectionFilter.TypeGolf:
          toggleType(AccommodationType.Golf, stayIngredient);
          break;

        default:
          exhaustiveGuard(
            type,
            'Unhandled filterUpdate.type in activitySelectionStore.ts'
          );
      }
    }

    function setListSuggestionType(suggestionType: SuggestionType): void {
      listSuggestionType.value = suggestionType;
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    function openActivityDetails(
      activityCard: ActivitySelectionCardData
    ): void {
      const ingredientIndex = recipeStore.getIngredientIndex(
        editingIngredientId.value
      );
      routerStore.navigateToActivityDetailsInActivitySelection(
        { ingredientIndex },
        activityCard.activityCode
      );
    }

    function resetAllFilters(): void {
      editingIngredientStore.editingStayIngredient?.resetAllFilters();
      activitySelectionFiltersStore.resetAllFiltersToDefault();
    }

    function toggleMap(): void {
      viewType.value =
        viewType.value === ActivitySelectionViewType.List
          ? ActivitySelectionViewType.Map
          : ActivitySelectionViewType.List;
    }

    // Getters
    const activitySelectionHeaderData = computed<
      ActivitySelectionDestinationData | undefined
    >(() => {
      const ingredient = editingIngredientStore.editingStayIngredient;
      return getActivitySelectionHeaderData(
        viewType.value,
        ingredient,
        ingredient
          ? itineraryStore.getHotelIngredientResponse(ingredient.ingredientId)
          : undefined,
        activitySelectionFiltersStore.activeFilters.length
      );
    });

    const activitySelectionCards = computed<ActivitySelectionCardData[]>(() => {
      let selectedActivityCodes: string[] = [];

      if (editingIngredientStore.editingStayIngredient?.ingredientId) {
        const hotelFromItinerary = itineraryStore.getHotelIngredientResponse(
          editingIngredientStore.editingStayIngredient.ingredientId
        );

        if (hotelFromItinerary) {
          selectedActivityCodes = hotelFromItinerary.subIngredientResponse
            .map((subIngredient) => subIngredient.suggested?.code)
            .filter(isDefined);
        }
      }

      let availableProducts = filterActivities(tripStore.destinationProducts);

      const availabilityOrder =
        listSuggestionType.value === SuggestionType.MostPopular
          ? AvailabilityOrder.Popular
          : AvailabilityOrder.Cheapest;

      availableProducts = sortActivities(availableProducts, availabilityOrder);

      return availableProducts.map((product) =>
        mapDestinationProductToActivitySelectionCard(
          product,
          globalCurrencyStore.formatMoney,
          getImageUrl,
          selectedActivityCodes
        )
      );
    });

    const shouldDisplayLoader = computed<boolean>(
      () =>
        tripStore.isSearchingDestinationAvailability ||
        isFeatureToggleActive('loader')
    );

    return {
      // State
      shouldShowPriceRibbon,
      viewType,
      listSuggestionType,

      // Actions
      initializeActivitySelectionView,
      resetActivitySelectionView,
      hidePriceCalendar,
      updateActivitySelectionFilter,
      showPriceRibbon,
      toggleFilters,
      setListSuggestionType,
      openActivityDetails,
      resetAllFilters,
      toggleMap,

      // Getters
      activitySelectionHeaderData,
      activitySelectionCards,
      shouldDisplayLoader,
    };
  }
);
